import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "../data/content";
import { scrollToId } from "../hooks/useLenis";
import SocialBar from "./SocialBar";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 60);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 inset-x-0 z-[9000]"
      >
        <div
          className={`mx-auto flex items-center justify-between px-5 md:px-10 transition-all duration-500 ${
            scrolled ? "py-3 glass-strong" : "py-5 bg-transparent"
          }`}
        >
          <button
            onClick={() => go("hero")}
            data-testid="nav-brand"
            className="font-display chrome-text text-lg md:text-xl tracking-[0.08em] leading-none"
          >
            {BRAND.name}
          </button>

          {/* desktop links */}
          <nav className="hidden md:flex items-center gap-9">
            {NAV.slice(1).map((n) => (
              <button
                key={n.target}
                onClick={() => go(n.target)}
                data-testid={`nav-${n.target}`}
                className="track-label text-white/55 hover:text-white transition-colors duration-300"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className="md:hidden text-white/80"
            aria-label="Open menu"
          >
            <Menu size={22} strokeWidth={1.4} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[9500] bg-[color:var(--void)]/95 backdrop-blur-2xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <span className="font-display chrome-text text-lg tracking-[0.08em]">{BRAND.name}</span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu" className="text-white/80">
                <X size={24} strokeWidth={1.4} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.target}
                  onClick={() => go(n.target)}
                  data-testid={`mobile-nav-${n.target}`}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display text-left steel-text text-5xl leading-[1.05] py-2"
                >
                  {n.label}
                </motion.button>
              ))}
            </nav>
            <p className="track-label text-white/30 px-8 pb-8">{BRAND.phrase}</p>
            <div className="px-8 pb-10">
              <SocialBar size={22} gap="gap-5" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
